import React, { createContext, useContext, useState, useEffect } from 'react';
import { useWholesaleAuth } from './WholesaleAuthContext';

const WholesaleCartContext = createContext();

export const useWholesaleCart = () => useContext(WholesaleCartContext);

export const WholesaleCartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Use a try-catch to handle cases where wholesale auth context is not available
  let currentWholesaleUser = null;
  try {
    const wholesaleAuth = useWholesaleAuth();
    currentWholesaleUser = wholesaleAuth.currentWholesaleUser;
  } catch (error) {
    // Wholesale auth context is not available, continue without it
    console.warn('WholesaleCartProvider: WholesaleAuthProvider not available');
  }

  // Load cart from localStorage when component mounts or user changes
  useEffect(() => {
    if (currentWholesaleUser) {
      const savedCart = localStorage.getItem(`wholesaleCart_${currentWholesaleUser.uid}`);
      if (savedCart) {
        setItems(JSON.parse(savedCart));
      }
    }
    setLoading(false);
  }, [currentWholesaleUser]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (currentWholesaleUser) {
      localStorage.setItem(`wholesaleCart_${currentWholesaleUser.uid}`, JSON.stringify(items));
    }
  }, [items, currentWholesaleUser]);

  const addItem = (product, quantity) => {
    setItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id);
      if (existingItem) {
        return prevItems.map(item =>
          item.id === product.id ? { ...item, quantity: item.quantity + quantity } : item
        );
      }
      return [...prevItems, { ...product, quantity }];
    });
  };

  const removeItem = (productId) => {
    setItems(prevItems => prevItems.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      return removeItem(productId);
    }
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const totalItems = items.reduce((total, item) => total + item.quantity, 0);
  const subtotal = items.reduce((total, item) => total + (item.wholesalePrice || item.price || 0) * item.quantity, 0);

  const value = {
    items,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    totalItems,
    subtotal,
    loading,
  };

  return (
    <WholesaleCartContext.Provider value={value}>
      {children}
    </WholesaleCartContext.Provider>
  );
}; 